import React, { useCallback, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Box, Modal, Tooltip, Typography, CircularProgress } from '@mui/material'
import Chip from '@mui/material/Chip'
import Stack from '@mui/material/Stack'
import Icon from '@mui/material/Icon'
import { useStyles } from '../Pathologist/inboxStyles'
import PatientCaseHistory from '../Common/PatientCaseHistoryModal'
import { DispatchColumns } from './table_def'
import { getDispatchData, unassignCase } from '../../../actions'
import { RootState } from '../../../reducers'
import { CaseStatus } from './../../../utils/displayLabels'
import TransferCaseModal from '../LabManager/TransferCaseModal'
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined'
import GroupAddOutlinedIcon from '@mui/icons-material/GroupAddOutlined'
import DeleteDialog from '../../HOC/DeleteDialog'
import GroupRemoveOutlinedIcon from '@mui/icons-material/GroupRemoveOutlined'
import AssignCaseModal from '../LabManager/AssignCaseModal'
import SuccessSnackBar from '../../HOC/SuccesSnackBar'
import ErrorSnackBar from '../../HOC/ErrorSnackBar'
import LaunchPremeris from '../../CaseDiagnosis/LaunchPremeris'
import { clearPremerisApiCache, clearPremerisReportApiCache, setCaseData, refreshCaseData } from '../../../actions'
import CustomDatagridDateRangeFilter from '../Common/CustomDatagridDateRangeFilter'
import {
  DataGrid,
  getGridStringOperators,
  GridFilterItem,
  GridFilterModel,
  GridFilterOperator,
  GridSortModel,
} from '@mui/x-data-grid'
import CustomDatagridDropDownFilter from '../Common/CustomDatagridDropDownFilter'

interface userObj {
  id?: number
  case_id?: number
  user_id?: string
  slide_status_id?: number
}

function DispatchLogistics({ userId, setUserInfo }) {
  const classes = useStyles()
  const [open, setOpen] = useState(false)
  const dispatch = useDispatch()
  const dispatchLogisticsData = useSelector((state: RootState) => state.getDispatchData)
  const [userDetails, setUserDetails] = useState<userObj>({})
  const [assignModalOpen, setAssignModalOpen] = useState(false)
  const { display_labels } = useSelector((state: RootState) => state.appConfig)
  const { role } = useSelector((state: RootState) => state.userLogin)
  const procedureData = useSelector((state: RootState) => state.getFilterProcedureCodeData)
  const refreshData = useSelector((state: RootState) => state.refreshCase)
  const [transferModalOpen, setTransferModalOpen] = useState(false)
  const [resetAssignmentOpen, setResetAssignmentOpen] = useState(false)
  const gridPageSize = 10
  const totalCount = dispatchLogisticsData.total_count
  const [openSuccessSnackBar, setOpenSuccessSnackBar] = useState(false)
  const [pageValue, setPageValue] = useState(0)
  const [isFirstPage, setFirstPage] = useState(false)
  const [premerisOpen, setPremerisOpen] = useState(false)
  const [isReadOnlyMode, setReadOnlyMode] = useState(false)
  const { re_unassignCase_success, re_unassignCase_error } = useSelector((state: RootState) => state.unassignResponse)
  const [filterValue, setFilterValue] = useState('')
  const [filterName, setFilterName] = useState('case_number')
  const [sortOrder, setSortOrder] = useState<string>('asc')
  const [isFilter, setFilter] = useState(false)
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [isLoading, setLoading] = useState(false)
  const [sortField, setSortField] = useState('case_number')

  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: sortField, sort: null }])

  const [filterModel, setFilterModel] = React.useState<GridFilterModel>({
    items: [
      {
        id: 1,
        columnField: 'quantity',
        value: [5000, 15000],
        operatorValue: 'between',
      },
    ],
  })

  const displayDispatchLogisticsData = dispatchLogisticsData?.data?.filter((val) => {
    val.index = val.case_number
    return val
  })

  const ProcedureCodeRenderer = {
    field: 'procedure_code',
    headerName: 'Case Type',
    sortable: true,
    width: 200,
    headerClassName: 'super-app-theme--header',
    disableClickEventBubbling: true,
    valueGetter: (params) => {
      const procedureCodeResults = procedureData.procedureCodeMap.filter((value) => {
        return value.id == params.row.procedure_code
      })
      return procedureCodeResults[0]?.display
    },
  }

  const CaseStatusRenderer = {
    field: 'workflow_stage_id',
    headerName: 'Case Status',
    width: 200,
    sortable: true,
    headerClassName: 'super-app-theme--header',
    disableClickEventBubbling: true,
    renderCell: (params) => {
      const caseStatus = CaseStatus(params.row.stage_id, role, display_labels)
      const statusArray = caseStatus.labelStr.split(', ')
      const style = { backgroundColor: caseStatus.labelColor }
      return (
        <>
          {statusArray.map((caseStatus) => (
            // eslint-disable-next-line react/jsx-key
            <>
              <Tooltip title={caseStatus} placement="top-start">
                <Stack direction="row" spacing={2}>
                  <Chip className={classes.chipSpacing} color="primary" style={style} label={caseStatus} />
                </Stack>
              </Tooltip>
            </>
          ))}
        </>
      )
    },
  }

  const DispatchIconRender = {
    field: '',
    headerName: 'Actions',
    disableColumnMenu: true,
    width: 125,
    disableClickEventBubbling: true,
    headerClassName: 'super-app-theme--header',
    filterable: false,
    sortable: false,
    renderCell: (params) => {
      const disableTransferStages = [
        'PRIMARY_DIAGNOSIS_RESULTED_2HRWINDOW',
        'PRIMARY_DIAGNOSIS_RESULTED_RESULT_RELEASED',
        'AMEND_RESULTED_RESULT_RELEASED',
        'CORRECT_RESULTED_2HRWINDOW',
        'CORRECT_RESULTED_RESULT_RELEASED',
      ]
      const unassignTransferStages = [
        'PRIMARY_DIAGNOSIS_ASSIGNED_INITIAL',
        'PRIMARY_DIAGNOSIS_ASSIGNED_READY',
        'PRIMARY_DIAGNOSIS_ASSIGNED_INPROGRESS',
        'PRIMARY_DIAGNOSIS_ASSIGNED_CONSULT',
      ]
      return (
        <>
          <Tooltip title="Case History" placement="top-start">
            <Icon onClick={(event) => handleOpen(event, params.row)}>info_outlined</Icon>
          </Tooltip>
          {params.row.stage_id !== 'PRIMARY_DIAGNOSIS_NO_SLOTS_INITIAL' &&
            disableTransferStages.includes(params.row.stage_id) === false && (
              <>
                &nbsp;&nbsp;&nbsp;
                <Tooltip title="Transfer case" placement="top-start">
                  <PeopleAltOutlinedIcon onClick={(event) => handleTransferModalOpen(event, params.row)}>
                    Transfer
                  </PeopleAltOutlinedIcon>
                </Tooltip>
              </>
            )}
          {params.row.stage_id === 'PRIMARY_DIAGNOSIS_NO_SLOTS_INITIAL' && (
            <>
              &nbsp;&nbsp;&nbsp;
              <Tooltip title="Assign case" placement="top-start">
                <GroupAddOutlinedIcon onClick={(event) => handleAssignModalOpen(event, params.row)}>
                  Assign
                </GroupAddOutlinedIcon>
              </Tooltip>
            </>
          )}
          {unassignTransferStages.includes(params.row.stage_id) && (
            <>
              &nbsp;&nbsp;&nbsp;
              <Tooltip title="Unassign case" placement="top-start">
                <GroupRemoveOutlinedIcon onClick={(event) => handleResetAssignmentOpen(event, params.row)}>
                  Unassign case
                </GroupRemoveOutlinedIcon>
              </Tooltip>
            </>
          )}
        </>
      )
    },
  }

  const handleFilter = useCallback(
    (obj) => {
      setFilter(true)
      setFilterName(obj.filterField)
      setFilterValue(obj.filterValue)
      setStartDate(obj.startDate)
      setEndDate(obj.endDate)
      setSortField(obj.sortField ? obj.sortField : 'case_number')
    },
    [isFilter],
  )

  const quantityOnlyOperators: GridFilterOperator[] = [
    {
      label: 'Between',
      value: 'between',
      getApplyFilterFn: (filterItem: GridFilterItem) => {
        if (!Array.isArray(filterItem.value) || filterItem.value.length !== 2) {
          return null
        }
        if (filterItem.value[0] == null || filterItem.value[1] == null) {
          return null
        }

        return null
      },
      InputComponent: CustomDatagridDateRangeFilter,
      InputComponentProps: { handleFilter, sortField: sortField, caseView: 'dispatch' },
    },
  ]

  const dropdownOnlyOperators: GridFilterOperator[] = [
    {
      label: 'List',
      value: 'list',
      getApplyFilterFn: (filterItem: GridFilterItem) => {
        if (!Array.isArray(filterItem.value) || filterItem.value.length !== 2) {
          return null
        }
        if (filterItem.value[0] == null || filterItem.value[1] == null) {
          return null
        }

        return null
      },
      InputComponent: CustomDatagridDropDownFilter,
      InputComponentProps: { handleFilter, sortField: sortField, caseView: 'dispatch' },
    },
  ]

  const ColumnsCustomFilters = (columnsData: any) => {
    const newDispatchColumns = [...columnsData]

    if (newDispatchColumns.length > 0) {
      const assignedDateIndex = newDispatchColumns.findIndex((col) => col.headerName === 'Assigned On')
      const assignedIndex = newDispatchColumns.findIndex((col) => col.headerName === 'Assign By')
      const caseTypeIndex = newDispatchColumns.findIndex((col) => col.headerName === 'Case Type')
      const priorityIndex = newDispatchColumns.findIndex((col) => col.headerName === 'Priority')
      const workflowIndex = newDispatchColumns.findIndex((col) => col.headerName === 'Case Status')
      const deliveredToIndex = newDispatchColumns.findIndex((col) => col.headerName === 'Deliver To')
      const statesIndex = newDispatchColumns.findIndex((col) => col.headerName === 'Location')
      const patientNameIndex = newDispatchColumns.findIndex((col) => col.headerName === 'Patient Name')
      const trfIdIndex = newDispatchColumns.findIndex((col) => col.headerName === 'TRF ID')

      if (assignedDateIndex !== -1) {
        newDispatchColumns[assignedDateIndex] = {
          ...newDispatchColumns[assignedDateIndex],
          filterOperators: quantityOnlyOperators,
        }
      }
      if (statesIndex !== -1) {
        newDispatchColumns[statesIndex] = {
          ...newDispatchColumns[statesIndex],
          filterOperators: dropdownOnlyOperators,
        }
      }
      if (assignedIndex !== -1) {
        newDispatchColumns[assignedIndex] = {
          ...newDispatchColumns[assignedIndex],
          filterOperators: dropdownOnlyOperators,
        }
      }
      if (deliveredToIndex !== -1) {
        newDispatchColumns[deliveredToIndex] = {
          ...newDispatchColumns[deliveredToIndex],
          filterOperators: dropdownOnlyOperators,
        }
      }
      if (caseTypeIndex !== -1) {
        newDispatchColumns[caseTypeIndex] = {
          ...newDispatchColumns[caseTypeIndex],
          filterOperators: dropdownOnlyOperators,
        }
      }
      if (priorityIndex !== -1) {
        newDispatchColumns[priorityIndex] = {
          ...newDispatchColumns[priorityIndex],
          filterOperators: dropdownOnlyOperators,
        }
      }
      if (workflowIndex !== -1) {
        newDispatchColumns[workflowIndex] = {
          ...newDispatchColumns[workflowIndex],
          filterOperators: dropdownOnlyOperators,
        }
      }
      if (patientNameIndex !== -1) {
        newDispatchColumns[patientNameIndex] = {
          ...newDispatchColumns[patientNameIndex],
          filterOperators: getGridStringOperators().filter((operator) => operator.value === 'contains'),
        }
      }
      if (trfIdIndex !== -1) {
        newDispatchColumns[trfIdIndex] = {
          ...newDispatchColumns[trfIdIndex],
          filterOperators: getGridStringOperators().filter((operator) => operator.value === 'startsWith'),
        }
      }
    }

    return newDispatchColumns
  }

  DispatchColumns.push(ProcedureCodeRenderer)
  DispatchColumns.push(CaseStatusRenderer)
  DispatchColumns.push(DispatchIconRender)

  const handleOpen = (event, rowData: any) => {
    setUserDetails(rowData)
    setOpen(true)
  }

  const handleClose = () => setOpen(false)

  const handleTransferModalOpen = (event, row: any) => {
    setUserDetails(row)
    setTransferModalOpen(true)
    setUserInfo(null)
  }

  const handleTransferModalClose = () => setTransferModalOpen(false)

  const handleResetAssignmentOpen = (event, row: any) => {
    setUserDetails(row)
    setResetAssignmentOpen(true)
    setUserInfo(null)
  }

  const handleResetAssignmentClose = () => setResetAssignmentOpen(false)

  const handlePageChange = (page, details) => {
    dispatch(
      getDispatchData({
        id: `${userId}`,
        pageIndex: page + 1,
        pageSize: gridPageSize,
        filterField: filterName !== '' ? filterName : sortField,
        filterValue: filterValue,
        caseView: 'dispatch',
        startDate: startDate,
        endDate: endDate,
        sortOrder: sortOrder,
        sortField: sortField,
      }),
    )
  }

  const handleAssignModalOpen = (event, row: any) => {
    setUserDetails(row)
    setAssignModalOpen(true)
    setUserInfo(null)
  }

  const handleAssignModalClose = () => setAssignModalOpen(false)

  const handleSubmit = async () => {
    await dispatch(unassignCase({ caseid: userDetails.case_id, currentid: userDetails.user_id, userid: userId }))
    setOpenSuccessSnackBar(true)
    setFirstPage(true)
    setTimeout(updateDataTable, 1500)
  }

  const updateDataTable = async () => {
    await dispatch(
      getDispatchData({
        id: `${userDetails.user_id}`,
        pageIndex: 1,
        pageSize: gridPageSize,
        filterField: filterName !== '' ? filterName : 'case_number',
        filterValue: filterValue,
        caseView: 'dispatch',
        startDate: startDate,
        endDate: endDate,
        sortOrder: 'asc',
        sortField: sortField,
      }),
    )
    handleResetAssignmentClose()
  }

  const handlePremerisOpen = async (event, caseId: any, data) => {
    const filterCompletedResults = data.filter((value) => {
      return value.case_id == caseId
    })
    const patientName = `${filterCompletedResults[0].patient_last_name || ''}, ${
      filterCompletedResults[0].patient_first_name || ''
    }`
    setLoading(true)
    await handleRefreshCases(filterCompletedResults[0].case_number + '000')
    dispatch(
      setCaseData({
        pathologist: {
          first_name: filterCompletedResults[0].first_name,
          last_name: filterCompletedResults[0].last_name,
        },
        pathologistId: filterCompletedResults[0].user_id,
        caseNumber: filterCompletedResults[0].case_number,
        procedureCodeId: filterCompletedResults[0].procedure_code,
        workflowId: filterCompletedResults[0].stage_id,
        patientName: patientName,
        caseId: caseId,
        nlOrderId: filterCompletedResults[0].nl_order_id,
        userCaseWorkflowId: filterCompletedResults[0].id,
        slideStatusId: filterCompletedResults[0].slide_status_id,
        mcFlag: filterCompletedResults[0].mc_flag,
        statFlag: filterCompletedResults[0].stat_flag,
      }),
    )
    setLoading(false)
    setPremerisOpen(true)
  }

  const handleRefreshCases = async (trfBarcode) => {
    await dispatch(refreshCaseData({ trfBarcode: trfBarcode }))
    await dispatch(clearPremerisApiCache())
    await dispatch(clearPremerisReportApiCache())
  }

  const handlePremerisClose = async () => {
    updateTrfStartDataTable()
    await dispatch(clearPremerisApiCache())
    await dispatch(clearPremerisReportApiCache())
    setPremerisOpen(false)
  }

  const updateTrfStartDataTable = async () => {
    dispatch(
      getDispatchData({
        id: `${userId}`,
        pageIndex: pageValue + 1,
        pageSize: gridPageSize,
        filterField: filterName !== '' ? filterName : 'case_number',
        filterValue: filterValue,
        caseView: 'dispatch',
        startDate: startDate,
        endDate: endDate,
        sortOrder: 'asc',
        sortField: sortField,
      }),
    )
  }

  const onFilterChange = async (model: GridFilterModel) => {
    setFilterName('')
    setFilterValue('')
    setStartDate('')
    setEndDate('')
    setFilterModel(model)
    if (model.items.length && model.items[0].value) {
      await onFilterApply(model)
    } else {
      await clearFilter()
      setFilter(false)
    }
  }

  const clearFilter = async () => {
    //const apiRef = useGridApiContext();
    await dispatch(
      getDispatchData({
        id: `${userId}`,
        pageIndex: pageValue,
        pageSize: gridPageSize,
        filterField: 'case_number',
        filterValue: null,
        caseView: 'dispatch',
        startDate: null,
        endDate: null,
        sortOrder: 'asc',
        sortField: sortField,
      }),
    )
    //apiRef.current.hideFilterPanel()
  }
  const onFilterApply = React.useCallback(async (model: GridFilterModel) => {
    if (model.items.length) {
      if (
        (model.items[0].columnField === 'patient_first_name' || model.items[0].columnField === 'case_number') &&
        model.items[0].value &&
        model.items[0].value.length
      ) {
        const val = model.items[0].value
        setFilterValue(val)
        const name = model.items[0].columnField
        setFilterName(name)
        await dispatch(
          getDispatchData({
            id: userId,
            caseView: 'dispatch',
            filterField: model.items[0].columnField,
            filterValue: val,
            startDate: null,
            endDate: null,
            sortOrder: 'asc',
            pageIndex: 0,
            pageSize: 10,
            sortField: sortField,
          }),
        )
        setFilter(true)
      }
    }
  }, [])

  const handleFilteredPageChange = async (page: any, details: any) => {
    setSortOrder(details[0].sort)

    await dispatch(
      getDispatchData({
        id: `${userId}`,
        caseView: 'dispatch',
        filterField: filterName !== '' ? filterName : details[0].field,
        filterValue: filterValue,
        startDate: startDate,
        endDate: endDate,
        sortOrder: details[0].sort ? details[0].sort : 'asc',
        pageIndex: page + 1,
        pageSize: gridPageSize,
        sortField: details[0].field,
      }),
    )
  }

  return (
    <div className={classes.container}>
      <Modal
        open={refreshData && refreshData.fetchInProgress}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <div>
          <CircularProgress style={{ marginLeft: '45%', marginTop: '40vh', position: 'absolute', color: '#0A688D' }} />
          <Typography
            style={{
              fontWeight: 'bolder',
              color: 'rgba(255,255,255,0.8)',
              fontSize: 'larger',
              marginLeft: '35%',
              marginTop: '47vh',
              position: 'absolute',
            }}
          >
            Refreshing the case details from LabOS...
          </Typography>
        </div>
      </Modal>
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box>
          <PatientCaseHistory userDetails={userDetails} handleClose={handleClose} />
        </Box>
      </Modal>
      <TransferCaseModal
        open={transferModalOpen}
        userId={userId}
        isSearch={false}
        trfId={null}
        onClose={handleTransferModalClose}
        userDetails={userDetails}
        setFirstPage={setFirstPage}
        filterName={filterName}
        filterValue={filterValue}
        sortField={sortField}
        startDate={endDate}
        endDate={endDate}
      />
      <AssignCaseModal
        open={assignModalOpen}
        userId={userId}
        isSearch={false}
        trfId={null}
        filterName={filterName}
        filterValue={filterValue}
        sortField={sortField}
        startDate={endDate}
        endDate={endDate}
        onClose={handleAssignModalClose}
        userDetails={userDetails}
        setFirstPage={setFirstPage}
      />
      {openSuccessSnackBar && re_unassignCase_success && <SuccessSnackBar message="Successfully unassigned the case" />}
      {openSuccessSnackBar && re_unassignCase_error && <ErrorSnackBar message="Unable to unassign the case" />}
      {resetAssignmentOpen && (
        <DeleteDialog
          header="Unassign"
          open={resetAssignmentOpen}
          onClose={handleResetAssignmentClose}
          handleSubmit={handleSubmit}
        />
      )}
      <div className={classes.mt}>
        <Box
          sx={{
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: '#0d688d73',
              fontWeight: '600',
            },
            '& .MuiDataGrid-columnHeader--moving': {
              backgroundColor: 'transparent !important',
            },
            '& .MuiDataGrid-cell:hover': {
              cursor: 'pointer !important',
            },
            '& .MuiDataGrid-filterForm': { p: 2 },
          }}
        >
          {display_labels && displayDispatchLogisticsData && (
            <DataGrid
              disableColumnSelector
              autoHeight
              pagination
              pageSize={10}
              paginationMode="server"
              rowCount={totalCount}
              rows={displayDispatchLogisticsData}
              columns={ColumnsCustomFilters(DispatchColumns)}
              loading={dispatchLogisticsData.fetchInProgress}
              page={isFirstPage ? 0 : pageValue}
              onPageChange={(page, details) => {
                setPageValue(page)
                setFirstPage(false)
                handlePageChange(page, details)
              }}
              onCellClick={(params, event) => {
                event.defaultMuiPrevented = true
                if (params.field === '') {
                  return
                } else {
                  setReadOnlyMode(true)
                  handlePremerisOpen(event, params.row.case_id, dispatchLogisticsData.data)
                }
              }}
              filterModel={filterModel}
              //sortModel={sortModel}
              onFilterModelChange={(model) => onFilterChange(model)}
              componentsProps={{
                filterPanel: {
                  filterFormProps: {
                    // Customize inputs by passing props
                    columnInputProps: {
                      variant: 'outlined',
                      size: 'small',
                      sx: { mt: 'auto' },
                    },
                    operatorInputProps: {
                      variant: 'outlined',
                      size: 'small',
                      sx: { mt: 'auto' },
                    },
                    valueInputProps: {
                      required: true,
                    },
                  },
                  sx: {
                    // Customize inputs using css selectors
                    '& .MuiDataGrid-filterForm': {
                      p: 2,
                      display: 'block',
                      width: 'fit-content',
                      justifyContent: 'flex-start',
                    },
                    '& .MuiDataGrid-filterForm:nth-child(even)': {},
                    '& .MuiDataGrid-filterFormLinkOperatorInput': { mr: 2 },
                    '& .MuiDataGrid-filterFormColumnInput': { mr: 2, width: 150 },
                    '& .MuiDataGrid-filterFormOperatorInput': { mr: 2 },
                    '& .MuiDataGrid-filterFormValueInput': { width: 'fit-content' },
                  },
                },
                panel: {
                  sx: {
                    '& .MuiDataGrid-panelWrapper': { overflow: 'visible' },
                    '& .MuiDataGrid-panelContent': { overflow: 'visible', display: 'flex' },
                    '& .MuiFormControl-root': {
                      width: 'auto',
                      minWidth: '55px',
                      marginRight: '18px',
                      justifyContent: 'end',
                    },
                  },
                },
              }}
              onSortModelChange={(newSortModel) => {
                if (newSortModel.length) setSortField(newSortModel[0].field)
                setPageValue(0)
                handleFilteredPageChange(pageValue, newSortModel)
              }}
            />
          )}
        </Box>
        <Modal
          open={premerisOpen}
          onClose={handlePremerisClose}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
        >
          <LaunchPremeris
            caseView={'dispatch'}
            urlLink={''}
            handlePremerisClose={handlePremerisClose}
            handleTRFComplete={null}
            isReadOnlyMode={isReadOnlyMode}
            isAmendFlow={false}
          />
        </Modal>
      </div>
    </div>
  )
}

export default DispatchLogistics
